import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Search, Shield, Globe, AlertTriangle, MessageSquare, Calendar, User, BarChart3, Cloud, ChevronDown, ChevronUp, Users, Download, FileText, FileJson, Network, Crown, CheckCircle, Bot, Image as ImageIcon, XCircle, FileDown } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import WordCloud from '@/components/WordCloud';
import { ParticipantNetworkGraph } from '@/components/ParticipantNetworkGraph';
import { formatDistanceToNow, format } from 'date-fns';
import { apiClient } from '@/lib/api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import DOMPurify from 'dompurify';

interface TelescopeMessage {
  id?: string;
  message_id?: number;
  group_id?: number;
  channel?: string;
  timestamp?: string;
  content?: string;
  tags?: string[];
  media?: string | null;
  is_verified?: boolean;
  description?: string;
  members_count?: number;
  has_previous?: boolean;
}

interface TelescopeSearchResult {
  messages?: TelescopeMessage[];
  total?: number;
  total_pages?: number;
  current_page?: number;
  execution_time_ms?: number;
  error?: string;
}

const TelescopeMonitoring: React.FC = () => {
  const [activeModule, setActiveModule] = useState<'cyberiq' | 'diplomatiq' | 'cybercrimeiq'>('cyberiq');
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<TelescopeSearchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [expandedMessages, setExpandedMessages] = useState<Set<string>>(new Set());
  const [activeViewTab, setActiveViewTab] = useState<'messages' | 'analytics'>('messages');
  
  // Group scraping state
  const [groupIdentifier, setGroupIdentifier] = useState('');
  const [scrapingLoading, setScrapingLoading] = useState(false);
  const [scrapingResult, setScrapingResult] = useState<any>(null);
  const [scrapingError, setScrapingError] = useState<string | null>(null);
  const [queryType, setQueryType] = useState<'group' | 'channel'>('group');

  const moduleConfig = {
    cyberiq: {
      title: 'CyberIQ',
      description: 'Track Telegram groups and channels relevant to cyber threats and incidents.',
      features: [
        'Raw communication from Telegram groups',
        'Mentions of brands and entities',
        'Covers hacktivist groups, ransomware groups, data breach groups, access sales, stealer logs, DDoS groups, and more',
        'More than 1,000+ groups monitored'
      ],
      module: 'cyber_crime',
      icon: Shield,
      color: 'text-blue-500'
    },
    diplomatiq: {
      title: 'DiplomatIQ',
      description: 'Monitor open-source intelligence channels for comprehensive OSINT analysis.',
      features: [
        'Covers over 50,000+ groups',
        'Extensive geographical coverage',
        'Threat Intelligence as a Service',
        'Regional language support',
        'Monitors politics, conflicts, defense, and more'
      ],
      module: 'diplomat',
      icon: Globe,
      color: 'text-green-500'
    },
    cybercrimeiq: {
      title: 'CyberCrimeIQ',
      description: 'Keep tabs on criminal activities across various Telegram channels.',
      features: [
        'Tracks drugs, terrorism, counterfeit, crypto scams, and more',
        'More than 1,000+ sources monitored',
        'On-demand regional focus',
        'Exclusive for governments'
      ],
      module: 'cyber_crime',
      icon: AlertTriangle,
      color: 'text-red-500'
    }
  };

  const handleSearch = async () => {
    if (!keyword.trim()) {
      setError('Please enter a keyword to search');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);
    setPage(1);

    try {
      const config = moduleConfig[activeModule];
      const result = await apiClient.searchTelescopeMessages({
        keyword: keyword.trim(),
        module: config.module,
        page: 1,
        limit: limit
      }) as TelescopeSearchResult;

      if (result.error) {
        setError(result.error);
      } else {
        setResults(result);
      }
    } catch (err: any) {
      console.error('Telescope search failed:', err);
      setError(err.message || 'Failed to search messages. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (!keyword.trim() || loading) return;

    setLoading(true);
    try {
      const config = moduleConfig[activeModule];
      const nextPage = page + 1;
      const result = await apiClient.searchTelescopeMessages({
        keyword: keyword.trim(),
        module: config.module,
        page: nextPage,
        limit: limit
      }) as TelescopeSearchResult;

      if (result.messages && result.messages.length > 0) {
        setResults(prev => ({
          ...prev,
          messages: [...(prev?.messages || []), ...result.messages!],
          total: result.total
        }));
        setPage(nextPage);
      }
    } catch (err: any) {
      console.error('Failed to load more:', err);
      setError(err.message || 'Failed to load more messages');
    } finally {
      setLoading(false);
    }
  };

  const formatMessageDate = (dateStr?: string) => {
    if (!dateStr) return 'Unknown date';
    try {
      const date = new Date(dateStr);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return dateStr;
    }
  };

  const formatFullDate = (dateStr?: string) => {
    if (!dateStr) return 'Unknown date';
    try {
      const date = new Date(dateStr);
      return format(date, 'PPpp');
    } catch {
      return dateStr;
    }
  };

  const toggleMessageExpansion = (messageId: string) => {
    setExpandedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  // Word Cloud Data
  const getWordCloudData = useMemo(() => {
    if (!results?.messages) return [];
    
    const wordCounts = new Map<string, number>();
    
    results.messages.forEach(message => {
      if (!message.content) return;
      
      // Extract words from content
      const words = message.content.toLowerCase()
        .replace(/[^\w\s#@]/g, ' ')
        .split(/\s+/)
        .filter(w => w.length > 3 && !['the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'can', 'her', 'was', 'one', 'our', 'out', 'day', 'get', 'has', 'him', 'his', 'how', 'its', 'may', 'new', 'now', 'old', 'see', 'two', 'way', 'who', 'boy', 'did', 'let', 'put', 'say', 'she', 'too', 'use'].includes(w));
      
      words.forEach(word => {
        wordCounts.set(word, (wordCounts.get(word) || 0) + 1);
      });
      
      // Add tags with higher weight
      (message.tags || []).forEach(tag => {
        if (tag && tag !== "no tags") {
          wordCounts.set(tag.toLowerCase(), (wordCounts.get(tag.toLowerCase()) || 0) + 5);
        }
      });
    });
    
    return Array.from(wordCounts.entries())
      .map(([text, value]) => ({ text, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 50);
  }, [results?.messages]);

  // Channel Distribution
  const getChannelDistribution = useMemo(() => {
    if (!results?.messages) return [];
    
    const channelCounts = new Map<string, number>();
    
    results.messages.forEach(message => {
      const channel = message.channel || 'Unknown';
      channelCounts.set(channel, (channelCounts.get(channel) || 0) + 1);
    });
    
    return Array.from(channelCounts.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [results?.messages]);

  // Messages Over Time
  const getMessagesOverTime = useMemo(() => {
    if (!results?.messages) return [];
    
    const timeCounts = new Map<string, number>();
    
    results.messages.forEach(message => {
      if (!message.timestamp) return;
      try {
        const date = new Date(message.timestamp);
        const dateKey = format(date, 'MMM dd, yyyy');
        timeCounts.set(dateKey, (timeCounts.get(dateKey) || 0) + 1);
      } catch {
        // Skip invalid dates
      }
    });
    
    return Array.from(timeCounts.entries())
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [results?.messages]);

  // Channel Members Distribution
  const getChannelMembersDistribution = useMemo(() => {
    if (!results?.messages) return [];
    
    const memberRanges = [
      { range: '0-1K', min: 0, max: 1000, count: 0 },
      { range: '1K-5K', min: 1001, max: 5000, count: 0 },
      { range: '5K-10K', min: 5001, max: 10000, count: 0 },
      { range: '10K-50K', min: 10001, max: 50000, count: 0 },
      { range: '50K+', min: 50001, max: Infinity, count: 0 }
    ];
    
    results.messages.forEach(message => {
      const members = message.members_count || 0;
      const range = memberRanges.find(r => members >= r.min && members <= r.max);
      if (range) {
        range.count++;
      }
    });
    
    return memberRanges.filter(r => r.count > 0).map(r => ({ name: r.range, value: r.count }));
  }, [results?.messages]);

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

  const handleGroupScrape = async () => {
    if (!groupIdentifier.trim()) {
      setScrapingError('Please enter a group or channel identifier');
      return;
    }

    setScrapingLoading(true);
    setScrapingError(null);
    setScrapingResult(null);

    try {
      const result = await apiClient.scrapeTelegramGroup(
        groupIdentifier.trim(),
        queryType
      ) as any;

      if (result.success) {
        setScrapingResult(result);
      } else {
        setScrapingError(result.error || 'Failed to scrape group');
      }
    } catch (err: any) {
      console.error('Group scraping failed:', err);
      setScrapingError(err.message || 'Failed to scrape group. Please try again.');
    } finally {
      setScrapingLoading(false);
    }
  };

  const downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const generatePDFReport = async (element: HTMLElement, result: any) => {
    try {
      // Show loading state
      const loadingToast = document.createElement('div');
      loadingToast.className = 'fixed top-4 right-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-lg z-50';
      loadingToast.textContent = 'Generating PDF report...';
      document.body.appendChild(loadingToast);

      // Create a temporary container for the report
      const reportContainer = document.createElement('div');
      reportContainer.style.width = '210mm'; // A4 width
      reportContainer.style.padding = '20mm';
      reportContainer.style.backgroundColor = '#ffffff';
      reportContainer.style.fontFamily = 'Arial, sans-serif';
      reportContainer.style.position = 'absolute';
      reportContainer.style.left = '-9999px';
      document.body.appendChild(reportContainer);

      // Add Sycek branding header
      const header = document.createElement('div');
      header.style.marginBottom = '30px';
      header.style.borderBottom = '3px solid #10b981';
      header.style.paddingBottom = '20px';
      header.innerHTML = `
        <div style="display: flex; align-items: center; gap: 15px; margin-bottom: 10px;">
          <div style="width: 60px; height: 60px; background: linear-gradient(135deg, #10b981, #059669); border-radius: 12px; display: flex; align-items: center; justify-content: center; color: white; font-size: 24px; font-weight: bold;">
            S
          </div>
          <div>
            <h1 style="margin: 0; font-size: 32px; font-weight: bold; color: #1f2937;">Sycek OSINT</h1>
            <p style="margin: 5px 0 0 0; color: #6b7280; font-size: 14px;">Telegram Group Participant Analysis Report</p>
          </div>
        </div>
        <div style="display: flex; gap: 20px; font-size: 12px; color: #6b7280;">
          <div>Generated: ${format(new Date(), 'PPpp')}</div>
          <div>Group: ${result?.group_identifier || 'N/A'}</div>
          <div>Participants: ${result?.participant_count || 0}</div>
        </div>
      `;
      reportContainer.appendChild(header);

      // Clone and style the analytics content
      const analyticsClone = element.cloneNode(true) as HTMLElement;
      analyticsClone.style.backgroundColor = '#ffffff';
      analyticsClone.style.color = '#1f2937';
      reportContainer.appendChild(analyticsClone);

      // Wait for images and charts to render
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Generate PDF
      const canvas = await html2canvas(reportContainer, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        useCORS: true,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // Add footer to each page
      const totalPages = pdf.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(
          `Sycek OSINT - Confidential Report - Page ${i} of ${totalPages}`,
          pdf.internal.pageSize.getWidth() / 2,
          pdf.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }

      const filename = `Sycek_Report_${result?.group_identifier?.replace(/[^a-zA-Z0-9]/g, '_') || 'group'}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      pdf.save(filename);

      // Cleanup
      document.body.removeChild(reportContainer);
      document.body.removeChild(loadingToast);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF report. Please try again.');
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Telescope Monitoring</h1>
          <p className="text-muted-foreground mt-2">
            Monitor Telegram channels and groups across specialized intelligence modules
          </p>
        </div>
      </div>

      <Tabs value={activeModule} onValueChange={(value) => {
        setActiveModule(value as 'cyberiq' | 'diplomatiq' | 'cybercrimeiq');
        setResults(null);
        setError(null);
        setKeyword('');
        setPage(1);
      }}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="cyberiq" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            CyberIQ
          </TabsTrigger>
          <TabsTrigger value="diplomatiq" className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            DiplomatIQ
          </TabsTrigger>
          <TabsTrigger value="cybercrimeiq" className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            CyberCrimeIQ
          </TabsTrigger>
        </TabsList>

        {Object.entries(moduleConfig).map(([key, config]) => {
          const ModuleIcon = config.icon;
          return (
            <TabsContent key={key} value={key} className="space-y-6">
              {/* Group Scraping Section */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <Users className="w-8 h-8 text-purple-500" />
                    <div>
                      <CardTitle>Group/Channel Participant Scraper</CardTitle>
                      <CardDescription>
                        Scrape participants from Telegram groups
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      <Input
                        placeholder="@group_username or https://t.me/group_username"
                        value={groupIdentifier}
                        onChange={(e) => setGroupIdentifier(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !scrapingLoading) {
                            handleGroupScrape();
                          }
                        }}
                        className="flex-1"
                      />
                      <select
                        value={queryType}
                        onChange={(e) => setQueryType(e.target.value as 'group' | 'channel')}
                        className="px-3 py-2 border rounded-md bg-background"
                      >
                        <option value="group">Group</option>
                        <option value="channel">Channel</option>
                      </select>
                      <Button
                        onClick={handleGroupScrape}
                        disabled={scrapingLoading || !groupIdentifier.trim()}
                      >
                        {scrapingLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Scraping...
                          </>
                        ) : (
                          <>
                            <Users className="w-4 h-4 mr-2" />
                            Scrape Participants
                          </>
                        )}
                      </Button>
                    </div>

                    {scrapingError && (
                      <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                        <p className="text-sm text-destructive">{scrapingError}</p>
                      </div>
                    )}

                    {scrapingResult && (
                      <div className="space-y-4">
                        <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                          <p className="text-sm font-medium text-green-500 mb-2">
                            Scraping completed successfully!
                          </p>
                          <div className="text-sm text-muted-foreground space-y-1">
                            <p>Group: {scrapingResult.group_identifier}</p>
                            <p>Participants found: {scrapingResult.participant_count}</p>
                            {scrapingResult.execution_time && (
                              <p>Execution time: {(scrapingResult.execution_time / 1000).toFixed(2)}s</p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {scrapingResult.html_file && (
                            <Card>
                              <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                  <FileText className="w-5 h-5" />
                                  HTML Report
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-2">
                                  <p className="text-sm text-muted-foreground">
                                    {scrapingResult.html_file.file_name || 'group_report.html'}
                                  </p>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      if (scrapingResult.html_file.content) {
                                        downloadFile(
                                          scrapingResult.html_file.content,
                                          scrapingResult.html_file.file_name || 'group_report.html',
                                          'text/html'
                                        );
                                      }
                                    }}
                                  >
                                    <Download className="w-4 h-4 mr-2" />
                                    Download HTML
                                  </Button>
                                  {scrapingResult.html_file.content && (
                                    <div className="mt-2">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          const newWindow = window.open();
                                          if (newWindow && scrapingResult.html_file.content) {
                                            const sanitized = DOMPurify.sanitize(scrapingResult.html_file.content, { ALLOWED_TAGS: ['p', 'div', 'span', 'br', 'h1', 'h2', 'h3', 'h4', 'table', 'tr', 'td', 'th', 'a', 'ul', 'ol', 'li', 'b', 'i', 'em', 'strong', 'img'], ALLOWED_ATTR: ['href', 'src', 'alt', 'class'] });
                                            newWindow.document.write(sanitized);
                                            newWindow.document.close();
                                          }
                                        }}
                                      >
                                        <FileText className="w-4 h-4 mr-2" />
                                        View in Browser
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          )}

                          {scrapingResult.json_file && (
                            <Card>
                              <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                  <FileJson className="w-5 h-5" />
                                  JSON Data
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-2">
                                  <p className="text-sm text-muted-foreground">
                                    {scrapingResult.json_file.file_name || 'group_data.json'}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {scrapingResult.participant_count} participants
                                  </p>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      if (scrapingResult.json_file.content) {
                                        downloadFile(
                                          scrapingResult.json_file.content,
                                          scrapingResult.json_file.file_name || 'group_data.json',
                                          'application/json'
                                        );
                                      }
                                    }}
                                  >
                                    <Download className="w-4 h-4 mr-2" />
                                    Download JSON
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          )}
                        </div>

                        {scrapingResult.json_file && scrapingResult.json_file.participants && (
                          <Card id="participant-report-section">
                            <CardHeader>
                              <div className="flex items-center justify-between">
                                <CardTitle className="flex items-center gap-2">
                                  <Users className="w-5 h-5" />
                                  Group Analytics & Participants
                                </CardTitle>
                                <Button
                                  onClick={() => {
                                    const reportElement = document.getElementById('participant-report-section');
                                    if (reportElement) {
                                      generatePDFReport(reportElement, scrapingResult);
                                    }
                                  }}
                                  className="flex items-center gap-2"
                                  variant="outline"
                                >
                                  <FileDown className="w-4 h-4" />
                                  Create PDF Report
                                </Button>
                              </div>
                            </CardHeader>
                            <CardContent className="space-y-6">
                              {/* Statistics Cards */}
                              {(() => {
                                const participants = Array.isArray(scrapingResult.json_file.participants) 
                                  ? scrapingResult.json_file.participants 
                                  : [];
                                
                                const stats = {
                                  total: participants.length,
                                  premium: participants.filter((p: any) => p.is_premium || p.Premium === 'True' || p.premium).length,
                                  verified: participants.filter((p: any) => p.is_verified || p.Verified === 'True' || p.verified).length,
                                  admins: participants.filter((p: any) => p.is_admin || p.Admin === 'True' || p.admin).length,
                                  bots: participants.filter((p: any) => p.is_bot || p.Bot === 'True' || p.bot).length,
                                  withPhoto: participants.filter((p: any) => p.has_photo || p['Has photo'] === 'True' || p.hasPhoto).length,
                                  deleted: participants.filter((p: any) => p.is_deleted || p['Is deleted'] === 'True' || p.isDeleted).length,
                                  scam: participants.filter((p: any) => p.is_scam || p.Scam === 'True' || p.scam).length,
                                  fake: participants.filter((p: any) => p.is_fake || p.Fake === 'True' || p.fake).length,
                                };

                                // DC Distribution
                                const dcDistribution = participants.reduce((acc: any, p: any) => {
                                  const dc = p.dc_id || p.DC || p.dcId;
                                  if (dc !== null && dc !== undefined) {
                                    acc[dc] = (acc[dc] || 0) + 1;
                                  }
                                  return acc;
                                }, {});

                                // Last seen distribution
                                const lastSeenDistribution = participants.reduce((acc: any, p: any) => {
                                  const lastSeen = p.last_seen || p.LastSeen || p.lastSeen || 'Unknown';
                                  acc[lastSeen] = (acc[lastSeen] || 0) + 1;
                                  return acc;
                                }, {});

                                return (
                                  <>
                                    {/* Stats Grid */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                      <Card>
                                        <CardContent className="p-4">
                                          <div className="text-2xl font-bold">{stats.total}</div>
                                          <div className="text-xs text-muted-foreground">Total Participants</div>
                                        </CardContent>
                                      </Card>
                                      <Card>
                                        <CardContent className="p-4">
                                          <div className="text-2xl font-bold text-yellow-600">{stats.premium}</div>
                                          <div className="text-xs text-muted-foreground">Premium</div>
                                        </CardContent>
                                      </Card>
                                      <Card>
                                        <CardContent className="p-4">
                                          <div className="text-2xl font-bold text-blue-600">{stats.verified}</div>
                                          <div className="text-xs text-muted-foreground">Verified</div>
                                        </CardContent>
                                      </Card>
                                      <Card>
                                        <CardContent className="p-4">
                                          <div className="text-2xl font-bold text-red-600">{stats.admins}</div>
                                          <div className="text-xs text-muted-foreground">Admins</div>
                                        </CardContent>
                                      </Card>
                                      <Card>
                                        <CardContent className="p-4">
                                          <div className="text-2xl font-bold text-purple-600">{stats.bots}</div>
                                          <div className="text-xs text-muted-foreground">Bots</div>
                                        </CardContent>
                                      </Card>
                                    </div>

                                    {/* Additional Stats */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                      <Card>
                                        <CardContent className="p-4">
                                          <div className="text-xl font-bold text-green-600">{stats.withPhoto}</div>
                                          <div className="text-xs text-muted-foreground">With Photo</div>
                                        </CardContent>
                                      </Card>
                                      <Card>
                                        <CardContent className="p-4">
                                          <div className="text-xl font-bold text-gray-600">{stats.deleted}</div>
                                          <div className="text-xs text-muted-foreground">Deleted</div>
                                        </CardContent>
                                      </Card>
                                      <Card>
                                        <CardContent className="p-4">
                                          <div className="text-xl font-bold text-orange-600">{stats.scam}</div>
                                          <div className="text-xs text-muted-foreground">Scam</div>
                                        </CardContent>
                                      </Card>
                                      <Card>
                                        <CardContent className="p-4">
                                          <div className="text-xl font-bold text-pink-600">{stats.fake}</div>
                                          <div className="text-xs text-muted-foreground">Fake</div>
                                        </CardContent>
                                      </Card>
                                    </div>

                                    {/* Charts */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                      {/* Premium vs Regular */}
                                      <Card>
                                        <CardHeader>
                                          <CardTitle className="text-sm">Premium vs Regular</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                          <ResponsiveContainer width="100%" height={200}>
                                            <PieChart>
                                              <Pie
                                                data={[
                                                  { name: 'Premium', value: stats.premium },
                                                  { name: 'Regular', value: stats.total - stats.premium }
                                                ]}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                                outerRadius={60}
                                                fill="#8884d8"
                                                dataKey="value"
                                              >
                                                <Cell fill="#f59e0b" />
                                                <Cell fill="#3b82f6" />
                                              </Pie>
                                              <Tooltip />
                                            </PieChart>
                                          </ResponsiveContainer>
                                        </CardContent>
                                      </Card>

                                      {/* Verified vs Unverified */}
                                      <Card>
                                        <CardHeader>
                                          <CardTitle className="text-sm">Verified Status</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                          <ResponsiveContainer width="100%" height={200}>
                                            <PieChart>
                                              <Pie
                                                data={[
                                                  { name: 'Verified', value: stats.verified },
                                                  { name: 'Unverified', value: stats.total - stats.verified }
                                                ]}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                                outerRadius={60}
                                                fill="#8884d8"
                                                dataKey="value"
                                              >
                                                <Cell fill="#2563eb" />
                                                <Cell fill="#94a3b8" />
                                              </Pie>
                                              <Tooltip />
                                            </PieChart>
                                          </ResponsiveContainer>
                                        </CardContent>
                                      </Card>

                                      {/* DC Distribution */}
                                      {Object.keys(dcDistribution).length > 0 && (
                                        <Card>
                                          <CardHeader>
                                            <CardTitle className="text-sm">Data Center Distribution</CardTitle>
                                          </CardHeader>
                                          <CardContent>
                                            <ResponsiveContainer width="100%" height={200}>
                                              <BarChart data={Object.entries(dcDistribution).map(([name, value]) => ({ name: `DC ${name}`, value }))}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="name" />
                                                <YAxis />
                                                <Tooltip />
                                                <Bar dataKey="value" fill="#8b5cf6" />
                                              </BarChart>
                                            </ResponsiveContainer>
                                          </CardContent>
                                        </Card>
                                      )}

                                      {/* Last Seen Distribution */}
                                      {Object.keys(lastSeenDistribution).length > 0 && (
                                        <Card>
                                          <CardHeader>
                                            <CardTitle className="text-sm">Last Seen Activity</CardTitle>
                                          </CardHeader>
                                          <CardContent>
                                            <ResponsiveContainer width="100%" height={200}>
                                              <BarChart data={Object.entries(lastSeenDistribution).map(([name, value]) => ({ name, value }))}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                                                <YAxis />
                                                <Tooltip />
                                                <Bar dataKey="value" fill="#10b981" />
                                              </BarChart>
                                            </ResponsiveContainer>
                                          </CardContent>
                                        </Card>
                                      )}
                                    </div>

                                    {/* Network Graph */}
                                    <Card>
                                      <CardHeader>
                                        <CardTitle className="text-sm flex items-center gap-2">
                                          <Network className="w-4 h-4" />
                                          Participant Network Graph
                                        </CardTitle>
                                        <CardDescription className="text-xs">
                                          Interactive network showing relationships between participants. 
                                          Red = Admin, Blue = Verified, Yellow = Premium, Purple = Bot, 
                                          Gray = Regular. Links show DC connections and admin relationships.
                                        </CardDescription>
                                      </CardHeader>
                                      <CardContent>
                                        <ParticipantNetworkGraph 
                                          participants={participants.map((p: any) => ({
                                            id: p.id || p['User ID'] || p.userId,
                                            username: p.username || p.Username || p.userName,
                                            full_name: p.full_name || p['Full Name'] || p.fullName,
                                            is_premium: p.is_premium || p.Premium === 'True' || p.premium,
                                            is_verified: p.is_verified || p.Verified === 'True' || p.verified,
                                            is_admin: p.is_admin || p.Admin === 'True' || p.admin,
                                            is_bot: p.is_bot || p.Bot === 'True' || p.bot,
                                            dc_id: p.dc_id || p.DC || p.dcId,
                                            last_seen: p.last_seen || p.LastSeen || p.lastSeen,
                                            has_photo: p.has_photo || p['Has photo'] === 'True' || p.hasPhoto,
                                            is_deleted: p.is_deleted || p['Is deleted'] === 'True' || p.isDeleted,
                                            is_scam: p.is_scam || p.Scam === 'True' || p.scam,
                                            is_fake: p.is_fake || p.Fake === 'True' || p.fake,
                                          }))}
                                          height={500}
                                        />
                                      </CardContent>
                                    </Card>

                                    {/* Participants Table */}
                                    <Card>
                                      <CardHeader>
                                        <CardTitle className="text-sm flex items-center gap-2">
                                          <Users className="w-4 h-4" />
                                          Participants List ({participants.length})
                                        </CardTitle>
                                      </CardHeader>
                                      <CardContent>
                                        <div className="overflow-x-auto">
                                          <table className="w-full border-collapse">
                                            <thead>
                                              <tr className="border-b-2 border-muted bg-muted/50">
                                                <th className="text-left p-3 text-xs font-semibold text-muted-foreground">User</th>
                                                <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Username</th>
                                                <th className="text-left p-3 text-xs font-semibold text-muted-foreground">ID</th>
                                                <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Status</th>
                                                <th className="text-left p-3 text-xs font-semibold text-muted-foreground">DC</th>
                                                <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Last Seen</th>
                                                <th className="text-left p-3 text-xs font-semibold text-muted-foreground">Details</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {participants.slice(0, 100).map((participant: any, idx: number) => {
                                                const fullName = participant.full_name || participant['Full Name'] || participant.fullName || 'N/A';
                                                const username = participant.username || participant.Username || participant.userName || null;
                                                const userId = participant.id || participant['User ID'] || participant.userId || 'N/A';
                                                const isPremium = participant.is_premium || participant.Premium === 'True' || participant.premium;
                                                const isVerified = participant.is_verified || participant.Verified === 'True' || participant.verified;
                                                const isAdmin = participant.is_admin || participant.Admin === 'True' || participant.admin;
                                                const isBot = participant.is_bot || participant.Bot === 'True' || participant.bot;
                                                const hasPhoto = participant.has_photo || participant['Has photo'] === 'True' || participant.hasPhoto;
                                                const lastSeen = participant.last_seen || participant.LastSeen || participant.lastSeen || 'Unknown';
                                                const dcId = participant.dc_id || participant.DC || participant.dcId || null;
                                                const isDeleted = participant.is_deleted || participant['Is deleted'] === 'True' || participant.isDeleted;
                                                const isScam = participant.is_scam || participant.Scam === 'True' || participant.scam;
                                                const isFake = participant.is_fake || participant.Fake === 'True' || participant.fake;

                                                return (
                                                  <tr 
                                                    key={idx} 
                                                    className={`border-b border-muted/50 hover:bg-muted/30 transition-colors ${isDeleted ? 'opacity-60' : ''}`}
                                                  >
                                                    <td className="p-3">
                                                      <div className="flex items-center gap-2">
                                                        <div className="flex items-center gap-1">
                                                          {isAdmin && <Crown className="w-3.5 h-3.5 text-red-600" />}
                                                          {isVerified && <CheckCircle className="w-3.5 h-3.5 text-blue-600" />}
                                                          {isBot && <Bot className="w-3.5 h-3.5 text-purple-600" />}
                                                          {hasPhoto && <ImageIcon className="w-3.5 h-3.5 text-green-600" />}
                                                          {isDeleted && <XCircle className="w-3.5 h-3.5 text-gray-600" />}
                                                        </div>
                                                        <span className="font-medium text-sm">{fullName}</span>
                                                      </div>
                                                    </td>
                                                    <td className="p-3">
                                                      {username ? (
                                                        <span className="text-sm text-muted-foreground font-mono">@{username}</span>
                                                      ) : (
                                                        <span className="text-xs text-muted-foreground italic">No username</span>
                                                      )}
                                                    </td>
                                                    <td className="p-3">
                                                      <span className="text-xs font-mono text-muted-foreground">{String(userId)}</span>
                                                    </td>
                                                    <td className="p-3">
                                                      <div className="flex flex-wrap gap-1">
                                                        {isPremium && <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-yellow-50 text-yellow-700 border-yellow-300">Premium</Badge>}
                                                        {isVerified && <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-700 border-blue-300">Verified</Badge>}
                                                        {isAdmin && <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-700 border-red-300">Admin</Badge>}
                                                        {isBot && <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-purple-50 text-purple-700 border-purple-300">Bot</Badge>}
                                                        {isScam && <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-orange-50 text-orange-700 border-orange-300">Scam</Badge>}
                                                        {isFake && <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-pink-50 text-pink-700 border-pink-300">Fake</Badge>}
                                                        {isDeleted && <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 bg-gray-50 text-gray-700 border-gray-300">Deleted</Badge>}
                                                      </div>
                                                    </td>
                                                    <td className="p-3">
                                                      {dcId !== null && dcId !== undefined ? (
                                                        <Badge variant="outline" className="text-xs">{dcId}</Badge>
                                                      ) : (
                                                        <span className="text-xs text-muted-foreground">-</span>
                                                      )}
                                                    </td>
                                                    <td className="p-3">
                                                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                        <Calendar className="w-3 h-3" />
                                                        <span>{lastSeen}</span>
                                                      </div>
                                                    </td>
                                                    <td className="p-3">
                                                      <div className="flex items-center gap-2 text-xs">
                                                        {hasPhoto && (
                                                          <span className="text-green-600" title="Has Profile Photo">
                                                            <ImageIcon className="w-3.5 h-3.5" />
                                                          </span>
                                                        )}
                                                      </div>
                                                    </td>
                                                  </tr>
                                                );
                                              })}
                                            </tbody>
                                          </table>
                                          {participants.length > 100 && (
                                            <p className="text-xs text-muted-foreground text-center pt-4">
                                              Showing first 100 of {participants.length} participants. 
                                              Download JSON to see all.
                                            </p>
                                          )}
                                        </div>
                                      </CardContent>
                                    </Card>
                                  </>
                                );
                              })()}
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Message Search Section */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <ModuleIcon className={`w-8 h-8 ${config.color}`} />
                    <div>
                      <CardTitle>{config.title}</CardTitle>
                      <CardDescription>{config.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {config.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <Input
                        placeholder={`Search ${config.title}...`}
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !loading) {
                            handleSearch();
                          }
                        }}
                        className="flex-1"
                      />
                      <Button
                        onClick={handleSearch}
                        disabled={loading || !keyword.trim()}
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Searching...
                          </>
                        ) : (
                          <>
                            <Search className="w-4 h-4 mr-2" />
                            Search
                          </>
                        )}
                      </Button>
                    </div>

                    {error && (
                      <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                        <p className="text-sm text-destructive font-medium mb-2">Error:</p>
                        <p className="text-sm text-destructive/90">{error}</p>
                        {error.includes("Authentication failed") || error.includes("Invalid or expired token") ? (
                          <p className="text-xs text-muted-foreground mt-2">
                            Please contact your administrator to update the Telescope API token in the backend configuration.
                          </p>
                        ) : null}
                      </div>
                    )}

                    {results && results.messages && results.messages.length > 0 && (
                      <div className="space-y-6">
                        {/* View Tabs */}
                        <Tabs value={activeViewTab} onValueChange={(v) => setActiveViewTab(v as 'messages' | 'analytics')}>
                          <TabsList>
                            <TabsTrigger value="messages" className="flex items-center gap-2">
                              <MessageSquare className="w-4 h-4" />
                              Messages ({results.messages.length})
                            </TabsTrigger>
                            <TabsTrigger value="analytics" className="flex items-center gap-2">
                              <BarChart3 className="w-4 h-4" />
                              Analytics
                            </TabsTrigger>
                          </TabsList>

                          {/* Analytics Tab */}
                          <TabsContent value="analytics" className="space-y-6">
                            {/* Summary Stats */}
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <Card>
                                <CardContent className="p-4">
                                  <div className="text-2xl font-bold">{results.messages.length}</div>
                                  <div className="text-sm text-muted-foreground">Total Messages</div>
                                </CardContent>
                              </Card>
                              <Card>
                                <CardContent className="p-4">
                                  <div className="text-2xl font-bold">{new Set(results.messages.map(m => m.channel)).size}</div>
                                  <div className="text-sm text-muted-foreground">Unique Channels</div>
                                </CardContent>
                              </Card>
                              <Card>
                                <CardContent className="p-4">
                                  <div className="text-2xl font-bold">
                                    {results.messages.filter(m => m.is_verified).length}
                                  </div>
                                  <div className="text-sm text-muted-foreground">Verified Channels</div>
                                </CardContent>
                              </Card>
                              <Card>
                                <CardContent className="p-4">
                                  <div className="text-2xl font-bold">
                                    {results.messages.filter(m => m.media).length}
                                  </div>
                                  <div className="text-sm text-muted-foreground">With Media</div>
                                </CardContent>
                              </Card>
                            </div>

                            {/* Word Cloud and Channel Distribution */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              {/* Word Cloud */}
                              <Card>
                                <CardHeader>
                                  <CardTitle className="flex items-center gap-2">
                                    <Cloud className="w-5 h-5" />
                                    Word Cloud
                                  </CardTitle>
                                </CardHeader>
                                <CardContent>
                                  {getWordCloudData.length > 0 ? (
                                    <div className="h-[300px] flex items-center justify-center">
                                      <WordCloud words={getWordCloudData} width={500} height={300} />
                                    </div>
                                  ) : (
                                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                                      No data for word cloud
                                    </div>
                                  )}
                                </CardContent>
                              </Card>

                              {/* Channel Distribution */}
                              <Card>
                                <CardHeader>
                                  <CardTitle>Top Channels</CardTitle>
                                </CardHeader>
                                <CardContent>
                                  {getChannelDistribution.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={300}>
                                      <BarChart data={getChannelDistribution}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                                        <YAxis />
                                        <Tooltip />
                                        <Bar dataKey="value" fill="#3b82f6" />
                                      </BarChart>
                                    </ResponsiveContainer>
                                  ) : (
                                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                                      No channel data
                                    </div>
                                  )}
                                </CardContent>
                              </Card>
                            </div>

                            {/* Messages Over Time and Channel Members */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              {/* Messages Over Time */}
                              <Card>
                                <CardHeader>
                                  <CardTitle>Messages Over Time</CardTitle>
                                </CardHeader>
                                <CardContent>
                                  {getMessagesOverTime.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={300}>
                                      <AreaChart data={getMessagesOverTime}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" />
                                        <YAxis />
                                        <Tooltip />
                                        <Area type="monotone" dataKey="count" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} />
                                      </AreaChart>
                                    </ResponsiveContainer>
                                  ) : (
                                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                                      No time data
                                    </div>
                                  )}
                                </CardContent>
                              </Card>

                              {/* Channel Members Distribution */}
                              <Card>
                                <CardHeader>
                                  <CardTitle>Channel Members Distribution</CardTitle>
                                </CardHeader>
                                <CardContent>
                                  {getChannelMembersDistribution.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={300}>
                                      <PieChart>
                                        <Pie
                                          data={getChannelMembersDistribution}
                                          cx="50%"
                                          cy="50%"
                                          labelLine={false}
                                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                          outerRadius={80}
                                          fill="#8884d8"
                                          dataKey="value"
                                        >
                                          {getChannelMembersDistribution.map((_, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                          ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend />
                                      </PieChart>
                                    </ResponsiveContainer>
                                  ) : (
                                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                                      No member data
                                    </div>
                                  )}
                                </CardContent>
                              </Card>
                            </div>
                          </TabsContent>

                          {/* Messages Tab */}
                          <TabsContent value="messages" className="space-y-4">
                            <div className="flex items-center justify-between">
                              <p className="text-sm text-muted-foreground">
                                {results.total !== undefined
                                  ? `Found ${results.total} message${results.total !== 1 ? 's' : ''} (Page ${results.current_page || 1} of ${results.total_pages || 1})`
                                  : `Showing ${results.messages?.length || 0} message${(results.messages?.length || 0) !== 1 ? 's' : ''}`
                                }
                              </p>
                            </div>

                            <div className="space-y-4">
                              {results.messages.map((message, idx) => {
                                const messageId = message.id || `msg-${idx}`;
                                const isExpanded = expandedMessages.has(messageId);
                                const contentPreview = message.content ? (message.content.length > 200 ? message.content.substring(0, 200) + '...' : message.content) : '';
                                
                                return (
                                  <Card key={messageId} className="hover:shadow-md transition-shadow">
                                    <CardContent className="p-4">
                                      <div className="space-y-3">
                                        <div className="flex items-start justify-between gap-4">
                                          <div className="flex-1 space-y-2">
                                            {message.channel && (
                                              <div className="flex items-center gap-2 flex-wrap">
                                                <User className="w-4 h-4 text-muted-foreground" />
                                                <span className="font-semibold">{message.channel}</span>
                                                {message.is_verified && (
                                                  <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-500 border-blue-500/20">
                                                    Verified
                                                  </Badge>
                                                )}
                                                {message.members_count !== undefined && message.members_count > 0 && (
                                                  <Badge variant="outline" className="text-xs">
                                                    {message.members_count.toLocaleString()} members
                                                  </Badge>
                                                )}
                                              </div>
                                            )}

                                            {message.description && (
                                              <p className="text-xs text-muted-foreground italic">
                                                {message.description}
                                              </p>
                                            )}

                                            {message.content && (
                                              <div>
                                                <p className="text-sm whitespace-pre-wrap break-words">
                                                  {isExpanded ? message.content : contentPreview}
                                                </p>
                                                {message.content.length > 200 && (
                                                  <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="mt-2 h-6 text-xs"
                                                    onClick={() => toggleMessageExpansion(messageId)}
                                                  >
                                                    {isExpanded ? (
                                                      <>
                                                        <ChevronUp className="w-3 h-3 mr-1" />
                                                        Show Less
                                                      </>
                                                    ) : (
                                                      <>
                                                        <ChevronDown className="w-3 h-3 mr-1" />
                                                        Show More
                                                      </>
                                                    )}
                                                  </Button>
                                                )}
                                              </div>
                                            )}

                                            <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                                              {message.timestamp && (
                                                <div className="flex items-center gap-1" title={formatFullDate(message.timestamp)}>
                                                  <Calendar className="w-3 h-3" />
                                                  {formatMessageDate(message.timestamp)}
                                                </div>
                                              )}
                                              {message.group_id && (
                                                <div className="flex items-center gap-1">
                                                  <User className="w-3 h-3" />
                                                  Group ID: {message.group_id}
                                                </div>
                                              )}
                                              {message.message_id && (
                                                <div className="flex items-center gap-1">
                                                  <MessageSquare className="w-3 h-3" />
                                                  Message ID: {message.message_id}
                                                </div>
                                              )}
                                            </div>

                                            {message.tags && message.tags.length > 0 && message.tags.filter(t => t && t !== "no tags").length > 0 && (
                                              <div className="flex flex-wrap gap-1">
                                                {message.tags.filter(t => t && t !== "no tags").map((tag, tagIdx) => (
                                                  <Badge key={tagIdx} variant="secondary" className="text-xs">
                                                    {tag}
                                                  </Badge>
                                                ))}
                                              </div>
                                            )}

                                            {message.media && (
                                              <div className="mt-2">
                                                <Badge variant="outline" className="text-xs">
                                                  Contains media
                                                </Badge>
                                              </div>
                                            )}

                                            {/* Expanded Details */}
                                            {isExpanded && (
                                              <div className="mt-4 pt-4 border-t space-y-2">
                                                <div className="grid grid-cols-2 gap-4 text-xs">
                                                  <div>
                                                    <span className="font-medium text-muted-foreground">Message ID:</span>
                                                    <span className="ml-2">{message.message_id || 'N/A'}</span>
                                                  </div>
                                                  <div>
                                                    <span className="font-medium text-muted-foreground">Group ID:</span>
                                                    <span className="ml-2">{message.group_id || 'N/A'}</span>
                                                  </div>
                                                  <div>
                                                    <span className="font-medium text-muted-foreground">Timestamp:</span>
                                                    <span className="ml-2">{message.timestamp ? formatFullDate(message.timestamp) : 'N/A'}</span>
                                                  </div>
                                                  <div>
                                                    <span className="font-medium text-muted-foreground">Has Media:</span>
                                                    <span className="ml-2">{message.media ? 'Yes' : 'No'}</span>
                                                  </div>
                                                  {message.members_count !== undefined && (
                                                    <div>
                                                      <span className="font-medium text-muted-foreground">Members:</span>
                                                      <span className="ml-2">{message.members_count.toLocaleString()}</span>
                                                    </div>
                                                  )}
                                                  <div>
                                                    <span className="font-medium text-muted-foreground">Verified:</span>
                                                    <span className="ml-2">{message.is_verified ? 'Yes' : 'No'}</span>
                                                  </div>
                                                </div>
                                                {message.description && (
                                                  <div className="mt-2">
                                                    <span className="font-medium text-muted-foreground text-xs">Channel Description:</span>
                                                    <p className="text-xs mt-1 text-muted-foreground">{message.description}</p>
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                );
                              })}

                              {results.total_pages !== undefined && 
                               results.current_page !== undefined &&
                               results.current_page < results.total_pages && (
                                <div className="flex justify-center pt-4">
                                  <Button
                                    variant="outline"
                                    onClick={handleLoadMore}
                                    disabled={loading}
                                  >
                                    {loading ? (
                                      <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Loading...
                                      </>
                                    ) : (
                                      'Load More'
                                    )}
                                  </Button>
                                </div>
                              )}
                            </div>
                          </TabsContent>
                        </Tabs>
                      </div>
                    )}

                    {results && (!results.messages || results.messages.length === 0) && (
                      <div className="text-center py-12 text-muted-foreground">
                        <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No messages found. Try a different keyword.</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
};

export default TelescopeMonitoring;


